import React, { useState } from 'react';
import { MapPin, Phone, Mail, Heart, Camera, ArrowUpRight, Instagram, Facebook, Lock } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { CONTACT, PHOTO_CREDITS } from '../mock';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

export default function Footer() {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();
  const [creditsOpen, setCreditsOpen] = useState(false);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const links = [
    { key: 'home', href: '#home' },
    { key: 'rooms', href: '#camere' },
    { key: 'experiences', href: '#cosa-fare' },
    { key: 'contact', href: '#contatti' },
  ];

  return (
    <footer className="bg-[#2A2420] text-[#E7DAC4]">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid md:grid-cols-3 gap-12">
        <div>
          <p className="font-display text-3xl text-white leading-none">la Bellavita</p>
          <p className="font-body text-[11px] tracking-[0.4em] uppercase text-[#D69A4C] mt-1 mb-5">rooms</p>
          <p className="font-body text-sm text-[#E7DAC4]/70 leading-relaxed max-w-xs">{t('footer.tagline')}</p>
          <div className="flex items-center gap-3 mt-6">
            <a
              href={CONTACT.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-[#E7DAC4] hover:bg-[#B5603A] hover:border-[#B5603A] hover:text-white transition-all duration-300"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={CONTACT.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-[#E7DAC4] hover:bg-[#B5603A] hover:border-[#B5603A] hover:text-white transition-all duration-300"
            >
              <Facebook className="w-5 h-5" />
            </a>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {links.map((l) => (
            <button
              key={l.key}
              onClick={() => scrollTo(l.href)}
              className="text-left font-body text-[#E7DAC4]/80 hover:text-[#D69A4C] transition-colors w-fit"
            >
              {t(`nav.${l.key}`)}
            </button>
          ))}
          <button
            onClick={() => setCreditsOpen(true)}
            className="text-left font-body text-[#E7DAC4]/80 hover:text-[#D69A4C] transition-colors w-fit flex items-center gap-1.5"
          >
            <Camera className="w-4 h-4" /> {t('footer.credits')}
          </button>
          <a
            href="/prenotazioni"
            className="text-left font-body text-[#E7DAC4]/50 hover:text-[#D69A4C] transition-colors w-fit flex items-center gap-1.5 text-sm"
          >
            <Lock className="w-3.5 h-3.5" /> {t('footer.reserved')}
          </a>
        </div>

        <div className="space-y-3 font-body text-sm">
          <a href={CONTACT.mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-[#E7DAC4]/80 hover:text-[#D69A4C] transition-colors">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {CONTACT.address}
          </a>
          <a href={`tel:${CONTACT.phoneHref}`} className="flex items-center gap-3 text-[#E7DAC4]/80 hover:text-[#D69A4C] transition-colors">
            <Phone className="w-4 h-4 shrink-0" /> {CONTACT.phone}
          </a>
          <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-[#E7DAC4]/80 hover:text-[#D69A4C] transition-colors">
            <Mail className="w-4 h-4 shrink-0" /> {CONTACT.email}
          </a>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-body text-xs text-[#E7DAC4]/50">
            {'\u00A9'} {year} la Bellavita rooms. {t('footer.rights')}
          </p>
          <p className="font-body text-xs text-[#E7DAC4]/50 flex items-center gap-1.5">
            {t('footer.made')} <Heart className="w-3.5 h-3.5 text-[#B5603A] fill-[#B5603A]" />
          </p>
        </div>
      </div>

      {/* Credits dialog */}
      <Dialog open={creditsOpen} onOpenChange={setCreditsOpen}>
        <DialogContent className="max-w-lg w-[95vw] bg-[#F6EFE3] border-none p-0 overflow-hidden">
          <div className="max-h-[80vh] overflow-y-auto">
            <DialogHeader className="px-7 pt-7 pb-4 border-b border-[#E7DAC4] text-left">
              <DialogTitle className="font-display text-3xl text-[#2A2420] flex items-center gap-2">
                <Camera className="w-6 h-6 text-[#B5603A]" /> {t('credits.title')}
              </DialogTitle>
              <p className="font-body text-sm text-[#6E655B] mt-2">{t('credits.intro')}</p>
            </DialogHeader>

            <div className="px-7 py-6 space-y-7">
              {/* Property photos */}
              <div>
                <p className="font-body text-xs tracking-[0.2em] uppercase text-[#B5603A] mb-3">{t('credits.ownTitle')}</p>
                <div className="flex items-center justify-between bg-white/60 rounded-[2px] px-4 py-3">
                  <span className="font-body text-sm text-[#2A2420]">{PHOTO_CREDITS.own[lang]}</span>
                  <a
                    href={PHOTO_CREDITS.own.authorUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-sm text-[#B5603A] hover:text-[#8F4A2C] transition-colors flex items-center gap-1 shrink-0"
                  >
                    @{PHOTO_CREDITS.own.author} <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* Unsplash */}
              <div>
                <p className="font-body text-xs tracking-[0.2em] uppercase text-[#B5603A] mb-2">{t('credits.unsplashTitle')}</p>
                <p className="font-body text-[13px] text-[#6E655B] mb-3 leading-relaxed">{t('credits.unsplashNote')}</p>
                <ul className="divide-y divide-[#E7DAC4]">
                  {PHOTO_CREDITS.unsplash.map((c, i) => (
                    <li key={i}>
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-between gap-3 py-2.5 hover:text-[#B5603A] transition-colors"
                      >
                        <span className="font-body text-sm text-[#2A2420] group-hover:text-[#B5603A]">{c.section[lang]}</span>
                        <span className="font-body text-[13px] text-[#B5603A] flex items-center gap-1 shrink-0">
                          {t('credits.view')} <ArrowUpRight className="w-3.5 h-3.5" />
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
